import { INPUT_TYPES } from '@shared/tabbed-table/constants';
import { IS_EMPTY_LIST, DATE_YMD, TO_CENTS } from 'cargo-chief-commons';
import { parseOperator } from '../../../loads-sourcing/store/filter-util';

import { CARRIER_LANES } from '../store/actions';

import { IS_NULL_OR_UNDEFINED, IS_EMPTY } from 'cargo-chief-commons';

import { environment } from '@environment/environment';

export const LANE_FIELDS = {
	ORIGIN: 'Origin',
	DESTINATION: 'Destination',
	LANE: 'Lane',
	EQUIPMENT: 'Equipment',
	FREQUENCY: 'Frequency',
	PARTIAL: 'Partial',
	LAST_MATCH: 'Last Match',
	LAST_RATE: 'Last Rate',
	LAST_BOOKED: 'Last Booked',
	MOST_RECENT_ACTIVITY: 'Most Recent Activity',

	ORIGIN_CITY: 'Origin City',
	ORIGIN_STATE: 'Origin State',
	DESTINATION_CITY: 'Destination City',
	DESTINATION_STATE: 'Destination State',

	EXCLUDED: 'Excluded',
	ACCOUNT_MAPPINGS: 'Connected Brokers',
};

export function buildHeaders() {
	let headers = [
		{ dataName: 'select', formattedName: 'select' },
		{ dataName: 'lane', formattedName: LANE_FIELDS.LANE },
		{ dataName: 'partial', formattedName: LANE_FIELDS.PARTIAL },
		{ dataName: 'lastMatchedAt', formattedName: LANE_FIELDS.LAST_MATCH },
		{ dataName: 'lastRate', formattedName: LANE_FIELDS.LAST_RATE },
		{ dataName: 'rateUpdatedAt', formattedName: LANE_FIELDS.LAST_BOOKED },
		{
			dataName: 'excludedLabel',
			formattedName: LANE_FIELDS.EXCLUDED,
			matIcon: 'info',
			matIconTooltip:
				'Excluding a lane will stop the Cargo Chief Booking Assistant from matching and notifying you of loads for this lane.',
		},
	];
	if (environment?.FEATURE_FLAGS?.CARRIER_LANE_BROKER_ACCOUNT_MAPPING_ENABLED) {
		headers.push({
			dataName: 'brokerAccountMaps',
			formattedName: LANE_FIELDS.ACCOUNT_MAPPINGS,
		});
	}
	return headers;
}

export function buildHeaderDataTypes() {
	let types = ['string', 'string', 'date', 'currency', 'date', 'string'];
	if (environment?.FEATURE_FLAGS?.CARRIER_LANE_BROKER_ACCOUNT_MAPPING_ENABLED) {
		types.push('brokerAccountMaps');
	}
	return types;
}

export function buildDisplayedColumns(isSmallScreen: boolean) {
	let columns = [];
	if (isSmallScreen) {
		columns = ['select', 'lane', 'partial', 'lastMatchedAt'];
	} else {
		columns = [
			'select',
			'lane',
			'partial',
			'lastMatchedAt',
			'lastRate',
			'rateUpdatedAt',
			'excludedLabel',
		];
		if (environment?.FEATURE_FLAGS?.CARRIER_LANE_BROKER_ACCOUNT_MAPPING_ENABLED) {
			columns.push('brokerAccountMaps');
		}
	}
	return columns;
}

const isExcluded = (lane: any) =>
	lane?.excluded === true || lane?.excluded === 'true' || lane?.excluded === 1;

export function buildActions(store) {
	return [
		{
			label: 'Delete',
			action: (lanes) => {
				store.dispatch(
					CARRIER_LANES.DELETE_LANES.CONFIRM({
						lanes,
						title: `Delete Selected Lane${lanes.length > 1 ? 's' : ''}`,
						body: `You are about to delete ${lanes.length} lane${lanes.length > 1 ? 's' : ''}. Are you sure you want to continue?`,
						cancelButtonText: 'Cancel',
						ConfirmButtonText: 'Delete',
					})
				);
			},
			disabled: (selected) => {
				return selected.length === 0;
			},
		},

		{
			dynamicLabel: (lanes) => {
				if (!lanes || lanes.length === 0) return 'Exclude/Include';

				const allExcluded = lanes.every((l) => isExcluded(l));
				const allIncluded = lanes.every((l) => !isExcluded(l));

				if (allExcluded) return 'Include';
				if (allIncluded) return 'Exclude';
				// Mixed selection fallback (shouldn’t happen but for fallback)
				return 'Exclude/Include';
			},
			action: (lanes) => {
				store.dispatch(
					CARRIER_LANES.EXCLUDE_LANES.CONFIRM({
						lanes,
						cancelButtonText: 'Cancel',
						ConfirmButtonText: 'Update',
					})
				);
			},
			disabled: (selected) => {
				return selected.length === 0;
			},
		},

		...(environment?.FEATURE_FLAGS?.CARRIER_LANE_BROKER_ACCOUNT_MAPPING_ENABLED
			? [
					{
						label: 'Assign to Account',
						action: (lanes: any[]) => {
							store.dispatch(
								CARRIER_LANES.ASSIGN_TO_ACCOUNT.CONFIRM({
									lanes,
								})
							);
						},
						disabled: (selected) => {
							return selected.length === 0;
						},
					},
				]
			: []),
	];
}

export const SORT_FIELDS = [
	LANE_FIELDS.ORIGIN_CITY,
	LANE_FIELDS.ORIGIN_STATE,
	LANE_FIELDS.DESTINATION_CITY,
	LANE_FIELDS.DESTINATION_STATE,
	LANE_FIELDS.EQUIPMENT,
	LANE_FIELDS.PARTIAL,
	LANE_FIELDS.LAST_MATCH,
	LANE_FIELDS.LAST_RATE,
	LANE_FIELDS.LAST_BOOKED,
	LANE_FIELDS.EXCLUDED,
];

export const FILTER_FIELDS = [
	LANE_FIELDS.ORIGIN_CITY,
	LANE_FIELDS.ORIGIN_STATE,
	LANE_FIELDS.DESTINATION_CITY,
	LANE_FIELDS.DESTINATION_STATE,
	LANE_FIELDS.EQUIPMENT,
	LANE_FIELDS.PARTIAL,
	LANE_FIELDS.LAST_MATCH,
	LANE_FIELDS.LAST_RATE,
	LANE_FIELDS.LAST_BOOKED,
	LANE_FIELDS.EXCLUDED,
];

export let filterFieldsInputTypeMap: Map<string, string> = new Map<string, string>();
filterFieldsInputTypeMap.set(LANE_FIELDS.ORIGIN_CITY, INPUT_TYPES.LOCATION);
filterFieldsInputTypeMap.set(LANE_FIELDS.ORIGIN_STATE, INPUT_TYPES.STATE);
filterFieldsInputTypeMap.set(LANE_FIELDS.DESTINATION_CITY, INPUT_TYPES.LOCATION);
filterFieldsInputTypeMap.set(LANE_FIELDS.DESTINATION_STATE, INPUT_TYPES.STATE);
filterFieldsInputTypeMap.set(LANE_FIELDS.EQUIPMENT, INPUT_TYPES.TRAILER_TYPE);
filterFieldsInputTypeMap.set(LANE_FIELDS.PARTIAL, INPUT_TYPES.BOOLEAN);
filterFieldsInputTypeMap.set(LANE_FIELDS.EXCLUDED, INPUT_TYPES.BOOLEAN);
filterFieldsInputTypeMap.set(LANE_FIELDS.LAST_RATE, INPUT_TYPES.NUMBER);
filterFieldsInputTypeMap.set(LANE_FIELDS.LAST_BOOKED, INPUT_TYPES.DATE);
filterFieldsInputTypeMap.set(LANE_FIELDS.LAST_MATCH, INPUT_TYPES.DATE);

export let filterFieldsInputLabelMap: Map<string, any> = new Map<string, any>();
filterFieldsInputLabelMap.set(LANE_FIELDS.PARTIAL, { True: 'Yes', False: 'No' });
filterFieldsInputLabelMap.set(LANE_FIELDS.EXCLUDED, { True: 'Yes', False: 'No' });

export const SERVER_FILTER_MAP = {
	[LANE_FIELDS.ORIGIN_CITY]: 'originCity',
	[LANE_FIELDS.ORIGIN_STATE]: 'originState',
	[LANE_FIELDS.DESTINATION_CITY]: 'destinationCity',
	[LANE_FIELDS.DESTINATION_STATE]: 'destinationState',
	[LANE_FIELDS.EQUIPMENT]: 'equipment',
	[LANE_FIELDS.FREQUENCY]: 'weeklyFrequency',
	[LANE_FIELDS.PARTIAL]: 'ltl',
	[LANE_FIELDS.LAST_MATCH]: 'lastMatchedAt',
	[LANE_FIELDS.LAST_RATE]: 'rate',
	[LANE_FIELDS.LAST_BOOKED]: 'rateUpdatedAt',
	[LANE_FIELDS.EXCLUDED]: 'excluded',
};

export const SERVER_SORT_MAP = {
	[LANE_FIELDS.ORIGIN_CITY]: 'originCity',
	[LANE_FIELDS.ORIGIN_STATE]: 'originState',
	[LANE_FIELDS.DESTINATION_CITY]: 'destinationCity',
	[LANE_FIELDS.DESTINATION_STATE]: 'destinationState',
	[LANE_FIELDS.EQUIPMENT]: 'equipment',
	[LANE_FIELDS.PARTIAL]: 'ltl',
	[LANE_FIELDS.FREQUENCY]: 'weeklyFrequency',
	[LANE_FIELDS.LAST_MATCH]: 'lastMatchedAt',
	[LANE_FIELDS.LAST_RATE]: 'rate',
	[LANE_FIELDS.LAST_BOOKED]: 'rateUpdatedAt',
	[LANE_FIELDS.EXCLUDED]: 'excludedAt',
};

export const parseValue = (
	filterField: string,
	value: any,
	convertEquipmentsToUUID: boolean = true
) => {
	if (IS_NULL_OR_UNDEFINED(value)) {
		return null;
	}
	if (Array.isArray(value)) {
		return value.join(',');
	}
	if (filterField === LANE_FIELDS.LAST_RATE) {
		value = TO_CENTS(value);
		if (isNaN(value)) return null;
		return `${value}`;
	}
	return `${value}`;
};

export const mapFilterData = (filtersList: any[]): any[] => {
	if (IS_EMPTY_LIST(filtersList)) return [];
	return filtersList.map((filter) => ({
		field: SERVER_FILTER_MAP[filter.filterField],
		value: parseValue(filter.filterField, filter.filterText, false),
		condition: filter.filterCondition || 'and',
		operator: parseOperator(filter.filterField, filter.operator),
		startValue: parseValue(filter.filterField, filter.startValue),
		endValue: parseValue(filter.filterField, filter.endValue),
	}));
};

export const mapSortData = (sortList: any[]): any[] => {
	if (IS_EMPTY_LIST(sortList)) return [];
	return sortList.map((sort) => ({
		field: SERVER_SORT_MAP[sort.sortField],
		direction: sort.direction,
	}));
};

export const EXCLUDE_LANES_LABEL: any = {
	YES: 'Yes',
	NO: 'No',
};
